import { useState } from 'react'
import { CheckCircle, Send, X } from 'lucide-react'

interface ShareWithBrokerCardProps {
  loanDetails: {
    bank: string
    product_name: string
    interest_rate: number
    comparison_rate: number
    monthly_payment: number
    total_cost: number
    loan_term_years: number
    loan_amount: number
    property_value: number
  }
  loanProductId: string
  brokerName: string
  user: { name: string; email: string; phone?: string }
  onSent: () => void
  onCancel: () => void
}

export default function ShareWithBrokerCard({
  loanDetails,
  loanProductId,
  brokerName,
  user,
  onSent,
  onCancel,
}: ShareWithBrokerCardProps) {
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const handleSend = async () => {
    setSending(true)
    setError('')
    try {
      const API_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
        ? ''
        : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

      const res = await fetch(`${API_BASE}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: user.name,
          customer_email: user.email,
          customer_phone: user.phone || '',
          preferred_contact_method: 'Email',
          preferred_contact_time: 'Anytime',
          loan_product_id: loanProductId,
          loan_details: loanDetails,
          notes: message || undefined,
          source: 'comparison_results',
          customer_type: 'authenticated',
        }),
      })

      if (!res.ok) throw new Error('Failed to send')

      setSent(true)
      setTimeout(onSent, 2500)
    } catch {
      setError('Failed to send. Please try again.')
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 animate-fade-in">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        <p className="text-sm text-green-800 font-medium">Sent to {brokerName}</p>
      </div>
    )
  }

  return (
    <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-800">
          Share <span className="text-blue-700">{loanDetails.product_name}</span> at{' '}
          <span className="text-blue-700">{loanDetails.interest_rate.toFixed(2)}%</span> with {brokerName}
        </p>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" title="Cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a note for your broker (optional)..."
        rows={2}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSend}
          disabled={sending}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {sending ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Send to {brokerName}
        </button>
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">
          Cancel
        </button>
      </div>
    </div>
  )
}
